import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { BusyOverlay } from '@/components/ui/busy-overlay';
import { Button } from '@/components/ui/button';
import { CopyButton } from '@/components/ui/copy-button';
import { FilterBar, type FilterField } from '@/components/ui/filter-bar';
import { Input } from '@/components/ui/input';
import { Pagination } from '@/components/ui/pagination';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatCurrency, formatDate } from '@/lib/format';
import { cn } from '@/lib/utils';
import { PAGE_SIZE, useGiftCardSearch } from './gift-cards.api';
import { TabStates } from './tab-shell';
import { SEARCH_TYPE, ownerName, type GiftCard } from './types';

/**
 * Cards — what exists, and what is left on it.
 *
 * Two controls, and no date range. A card is looked up by its code, or listed
 * by whether it still holds money; when it happened to be printed is not a
 * question anyone asks, and the endpoint made the range worse by quietly
 * switching which column it filtered — `ActivatedOn` for the activated states,
 * `CreatedAt` otherwise — so the same dates meant two different things
 * depending on the neighbouring select.
 */

export function CardsTab({
  onOpen,
}: {
  onOpen: (card: { id: string; code: string | null }) => void;
}) {
  const { t } = useTranslation();

  const [draft, setDraft] = useState('');
  const [query, setQuery] = useState('');
  const [searchType, setSearchType] = useState(String(SEARCH_TYPE.all));
  const [page, setPage] = useState(0);

  const search = useGiftCardSearch({
    query,
    searchType: Number(searchType),
    page,
  });

  const rows = search.data?.giftCards ?? [];
  const total = search.data?.total ?? 0;

  const reset = <T,>(setter: (value: T) => void) => (value: T) => {
    setter(value);
    setPage(0);
  };

  const fields: FilterField[] = [
    {
      id: 'searchType',
      label: t('giftCards.cardState'),
      value: searchType,
      onChange: reset(setSearchType),
      // All → activated → …  is a lifecycle, not a list of names.
      sort: false,
      resetValue: String(SEARCH_TYPE.all),
      options: [
        { value: String(SEARCH_TYPE.all), label: t('giftCards.stateAll') },
        {
          value: String(SEARCH_TYPE.activated),
          label: t('giftCards.stateActivated'),
        },
        {
          value: String(SEARCH_TYPE.activatedWithBalance),
          label: t('giftCards.stateWithBalance'),
        },
        {
          value: String(SEARCH_TYPE.activatedNoBalance),
          label: t('giftCards.stateNoBalance'),
        },
        {
          value: String(SEARCH_TYPE.nonActivated),
          label: t('giftCards.stateNotActivated'),
        },
      ],
    },
  ];

  const CardCode = ({ row }: { row: GiftCard }) => (
    <div className="min-w-0">
      <span className="group flex items-center gap-1">
        <span className="truncate font-mono text-xs">{row.code ?? '—'}</span>
        {row.code && <CopyButton value={row.code} />}
      </span>
      {row.nickname && (
        <p className="text-muted-foreground truncate text-xs">{row.nickname}</p>
      )}
    </div>
  );

  return (
    <div className="space-y-4 pt-4">
      <FilterBar
        busy={search.isFetching}
        search={
          <form
            role="search"
            onSubmit={(event) => {
              event.preventDefault();
              if (draft === query && page === 0) void search.refetch();
              setQuery(draft);
              setPage(0);
            }}
            className="flex gap-2"
          >
            <div className="relative flex-1">
              <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
              <Input
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                placeholder={t('giftCards.searchPlaceholder')}
                aria-label={t('giftCards.searchPlaceholder')}
                className="h-10 pl-9"
              />
            </div>
            <Button type="submit" className="h-10">
              <Search className="sm:hidden" />
              <span className="hidden sm:inline">{t('common.search')}</span>
            </Button>
          </form>
        }
        fields={fields}
        summary={
          search.isSuccess ? (
            <p className="text-muted-foreground text-sm" aria-live="polite">
              {t('giftCards.showingOf', { shown: rows.length, total })}
            </p>
          ) : null
        }
      />

      <TabStates
        isError={search.isError}
        isPending={search.isPending}
        isEmpty={search.isSuccess && rows.length === 0}
        busy={search.isFetching}
        emptyMessage={t('giftCards.noCards')}
        onRetry={() => void search.refetch()}
      >
        {rows.length > 0 && (
          <>
            <div className="hidden rounded-xl border lg:block">
              <Table busy={search.isFetching}>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead>{t('giftCards.code')}</TableHead>
                    <TableHead>{t('giftCards.owner')}</TableHead>
                    <TableHead>{t('giftCards.created')}</TableHead>
                    <TableHead>{t('giftCards.activated')}</TableHead>
                    <TableHead className="text-right">
                      {t('giftCards.opening')}
                    </TableHead>
                    <TableHead className="text-right">
                      {t('giftCards.balance')}
                    </TableHead>
                    <TableHead className="w-20 text-right" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={row.id} className="group">
                      <TableCell>
                        <CardCode row={row} />
                      </TableCell>
                      <TableCell>
                        {row.owner ? (
                          <div className="min-w-0">
                            <p className="truncate">{ownerName(row.owner)}</p>
                            <p className="text-muted-foreground truncate text-xs">
                              {row.owner.email}
                            </p>
                          </div>
                        ) : (
                          <span className="text-muted-foreground">
                            {t('giftCards.unassigned')}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        {formatDate(row.createdAt)}
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        {row.activatedOn ? (
                          formatDate(row.activatedOn)
                        ) : (
                          <Badge variant="secondary">
                            {t('giftCards.notActivated')}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatCurrency(row.openingBalance)}
                      </TableCell>
                      <TableCell className="text-right font-medium tabular-nums">
                        {formatCurrency(row.currentBalanceLeft)}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" onClick={() => onOpen({ id: row.id, code: row.code })}>
                          {t('giftCards.view')}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <div className="relative space-y-2 lg:hidden">
              <BusyOverlay show={search.isFetching} />
              {rows.map((row) => (
                <div
                  key={row.id}
                  className={cn(
                    'bg-card space-y-3 rounded-xl border p-3',
                    search.isFetching && 'opacity-60 transition-opacity'
                  )}
                >
                  <div className="flex items-start justify-between gap-3">
                    <CardCode row={row} />
                    <p className="shrink-0 font-medium tabular-nums">
                      {formatCurrency(row.currentBalanceLeft)}
                    </p>
                  </div>
                  <div className="text-muted-foreground space-y-0.5 text-xs">
                    <p className="truncate">
                      {row.owner
                        ? ownerName(row.owner)
                        : t('giftCards.unassigned')}
                    </p>
                    <p>
                      {row.activatedOn
                        ? t('giftCards.activatedOn', {
                            date: formatDate(row.activatedOn),
                          })
                        : t('giftCards.notActivated')}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    className="w-full"
                    onClick={() => onOpen({ id: row.id, code: row.code })}
                  >
                    {t('giftCards.view')}
                  </Button>
                </div>
              ))}
            </div>

            <Pagination
              page={page}
              size={PAGE_SIZE}
              total={total}
              onPageChange={setPage}
            />
          </>
        )}
      </TabStates>
    </div>
  );
}
